import { z } from "zod";
import { EXPENSE_STATUS, expenseSpentAtSchema } from "./expenses.js";

/* ===========================================================================
 * US-E7.F3-0051 — Edit / Delete Expense (Reversal) (@UC0036)
 * POST /v1/expenses/:id/reversal | PATCH /v1/expenses/:id | DELETE /v1/expenses/:id
 * Phase 8 Consensus + Phase 6 LEDGER — @gate money,ledger 🔴.
 *
 * reversal (approved/posted) → EXPENSE_REVERSAL append-only qua LedgerService.reverse (móng 0041):
 *   đảo line gốc (DEBIT CASH / CREDIT EXPENSE) → CASH khôi phục + expense→reversed + link.
 *   NEVER hard-delete/UPDATE ledger gốc.
 * PATCH → sửa metadata (name/category/description) + audit. amountMinor immutable → reject 400.
 * DELETE → hard-delete CHỈ khi status=pending_decision (chưa ledger); posted/reversed → reject.
 * ========================================================================= */

/** amountMinor immutable — sửa tiền = huỷ + tạo lại (UC0036). Message client + server dùng chung. */
export const EXPENSE_AMOUNT_IMMUTABLE_MESSAGE =
  "Để thay đổi số tiền, huỷ và tạo lại khoản chi.";

/**
 * 🔴 US-E24.F52-0504 (@UC0036 AC13) — MÃ LỖI ỔN ĐỊNH khi xoá khoản chi ĐÃ ghi sổ.
 *
 * Luật không đổi (posted ⇒ không xoá cứng, ledger append-only), chỉ có **cách nói** đổi: trước đây
 * client chỉ nhận `code: "CONFLICT"` chung ⇒ hiện được mỗi câu lỗi chung chung, trong khi ĐÚNG việc
 * người dùng cần làm tiếp là **huỷ bằng bút toán đảo** (`POST /v1/expenses/:id/reversal`). Mã này
 * là thứ client rẽ nhánh, không phải `detail` (§7: `detail` chỉ để debug).
 *
 * 🔴 Ngôn-ngữ-trung-tính: backend KHÔNG trả câu đã dịch; client map `code → message`.
 */
export const EXPENSE_POSTED_REVERSAL_REQUIRED_CODE =
  "EXPENSE_POSTED_REVERSAL_REQUIRED" as const;

/**
 * 🔴 EXPENSE-CANCEL-AFTER-AMEND-REVERSAL (@UC0036) — hai MÃ LỖI ỔN ĐỊNH của `POST /v1/expenses/:id/reversal` khi KHÔNG suy được
 * đúng MỘT bút toán chi đang sống để đảo (409, 0 bút toán). Ngôn-ngữ-trung-tính: client map `code → message`.
 *  · `EXPENSE_LEDGER_ALREADY_REVERSED` — khoản chi còn `posted` nhưng MỌI bút toán chi của nó đã bị đảo bởi đường khác (yêu cầu sửa
 *    của quản trị, khắc phục khiếu nại…) ⇒ tiền đã về quỹ, chỉ trạng thái khoản chi chưa theo. Không tự đồng bộ trạng thái (câu PO mở).
 *  · `EXPENSE_MULTIPLE_LIVE_LEDGER_TX` — ≥2 bút toán chi cùng còn hiệu lực ⇒ không đoán cái nào để đảo, cần đối soát.
 */
export const EXPENSE_LEDGER_ALREADY_REVERSED_CODE = "EXPENSE_LEDGER_ALREADY_REVERSED" as const;
/**
 * 🔴 FIX-1272 L-F1 (FUND-DEBT-REPAY-PATH · §3.3) — khoản chi khẩn cấp BỊ BÁC đã sinh nợ quỹ (`emergency_repay`, chuyển thành khoản phải đóng): tiền
 * về quỹ đi qua KHOẢN PHẢI ĐÓNG đó. Đảo / sửa nhầm / huỷ bút toán EXPENSE gốc nữa là đếm tiền về HAI lần (CASH + số đã chi lần thứ hai) ⇒ 409, 0
 * bút toán, ở MỌI đường đảo (end-user huỷ · sửa nhầm · admin · khắc phục khiếu nại · chia lại). Không `params`.
 */
export const EXPENSE_HAS_FUND_DEBT_CODE = "EXPENSE_HAS_FUND_DEBT" as const;
export const EXPENSE_MULTIPLE_LIVE_LEDGER_TX_CODE = "EXPENSE_MULTIPLE_LIVE_LEDGER_TX" as const;

/**
 * 🔴 CONFLICT-CODE-ROUND-2 (@UC0036) — hai mã còn lại của `POST /v1/expenses/:id/reversal` (409, 0 bút toán; HTTP + `detail` giữ nguyên):
 *  · `EXPENSE_REVERSAL_NOT_POSTED` — khoản chi KHÔNG ở trạng thái đã ghi sổ (`params.status` = trạng thái hiện tại: chờ quyết / bị từ chối ⇒
 *    chưa có bút toán để đảo; `reversed` ⇒ đã hoàn rồi). Client nói theo `status`.
 *  · `EXPENSE_LEDGER_TX_MISSING` — `posted` mà KHÔNG có bút toán chi nào (dữ liệu hỏng) ⇒ chặn, cần đối soát.
 */
export const EXPENSE_REVERSAL_NOT_POSTED_CODE = "EXPENSE_REVERSAL_NOT_POSTED" as const;
export const EXPENSE_LEDGER_TX_MISSING_CODE = "EXPENSE_LEDGER_TX_MISSING" as const;

/* ---- PATCH metadata only ---- */
/**
 * Body PATCH: name/category/description + `spentAt` (nhãn NGÀY). `.strict()` → field lạ (kể cả
 * `amountMinor`) → reject 400 (amountMinor immutable — B1). Mọi field optional nhưng phải có ≥1
 * (refine) — tránh PATCH rỗng.
 *
 * 🔴 `spentAt` KHÔNG phải tiền và KHÔNG mở cửa cho tiền: nó là nhãn hiển thị, không tham gia tính
 * số dư/kỳ báo cáo/thứ tự cursor. Sửa SỐ TIỀN vẫn đi **đúng một đường**
 * `POST /v1/funds/:id/ledger-corrections` (REVERSAL + POST trong một transaction).
 * `null` = xoá ngày về *"không ai khai"* — **khác** *"chi hôm nay"*, nên không lấp bằng `now()`.
 */
export const patchExpenseRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    category: z.string().trim().max(100).nullable().optional(),
    description: z.string().trim().max(1000).nullable().optional(),
    // Luật "không ở tương lai" dùng CHUNG với lúc tạo (`expenseSpentAtSchema`) — không chép lại.
    spentAt: expenseSpentAtSchema.nullable().optional(),
  })
  .strict()
  .refine(
    (v) =>
      v.name !== undefined ||
      v.category !== undefined ||
      v.description !== undefined ||
      v.spentAt !== undefined,
    "Không có thay đổi nào để lưu.",
  );

export type PatchExpenseRequest = z.infer<typeof patchExpenseRequestSchema>;

export const patchExpenseResponseSchema = z
  .object({
    expenseId: z.string().uuid(),
    name: z.string().nullable(),
    category: z.string().nullable(),
    description: z.string().nullable(),
    /** Nhãn ngày sau khi sửa — ghi rồi phải đọc lại được NGAY, không đợi GET thứ hai. */
    spentAt: z.string().datetime().nullable(),
    status: z.string(),
    updatedAt: z.string().datetime(),
  })
  .strict();

export type PatchExpenseResponse = z.infer<typeof patchExpenseResponseSchema>;

/* ---- reversal → EXPENSE_REVERSAL append-only ---- */
export const reverseExpenseResponseSchema = z
  .object({
    expenseId: z.string().uuid(),
    /** tx REVERSAL mới (đảo line gốc). */
    reversalTxId: z.string().uuid(),
    /** tx EXPENSE gốc bị đảo (link, vẫn còn nguyên — append-only). */
    reversesTxId: z.string().uuid(),
    newStatus: z.literal(EXPENSE_STATUS.REVERSED),
    reversedAt: z.string().datetime(),
  })
  .strict();

export type ReverseExpenseResponse = z.infer<
  typeof reverseExpenseResponseSchema
>;

/* ---- hard-delete (pending_decision only) ---- */
export const deleteExpenseResponseSchema = z
  .object({
    expenseId: z.string().uuid(),
    deleted: z.literal(true),
  })
  .strict();

export type DeleteExpenseResponse = z.infer<
  typeof deleteExpenseResponseSchema
>;
